import { type ReactNode, useState } from 'react';
import { Avatar, Button, Drawer, Dropdown, Layout, Menu, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { MenuOutlined, UserOutlined } from '@ant-design/icons';
import { useBreakpoint } from './useBreakpoint';

const { Header, Sider, Content } = Layout;

export interface AppShellBrand {
  mark: ReactNode;
  title: string;
  subtitle: string;
}

export interface ResponsiveAppShellProps {
  brand: AppShellBrand;
  menuItems: MenuProps['items'];
  selectedKey: string;
  /**
   * Navigate handler invoked with the clicked menu key. Required so that the
   * collapsed pad icon-rail navigates on click (an icon-only item has no inline
   * <Link> to activate). Menu item labels can therefore be plain text.
   */
  onNavigate: (key: string) => void;
  userMenu: MenuProps;
  userLabel: ReactNode;
  contextPill?: ReactNode;
  topbarTitle: string;
  topbarSubtitle: ReactNode;
  /** Page content (typically a router <Outlet/>). */
  children: ReactNode;
  /** Extra nodes (e.g. modals) rendered alongside the layout. */
  extras?: ReactNode;
}

function BrandBlock({ brand, compact = false }: { brand: AppShellBrand; compact?: boolean }) {
  return (
    <div className="mall-brand">
      <div className="mall-brand-mark">{brand.mark}</div>
      {compact ? null : (
        <div>
          <div className="mall-brand-title">{brand.title}</div>
          <div className="mall-brand-subtitle">{brand.subtitle}</div>
        </div>
      )}
    </div>
  );
}

export function ResponsiveAppShell({
  brand,
  menuItems,
  selectedKey,
  onNavigate,
  userMenu,
  userLabel,
  contextPill,
  topbarTitle,
  topbarSubtitle,
  children,
  extras,
}: ResponsiveAppShellProps) {
  const breakpoint = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMobile = breakpoint === 'mobile';
  const isPad = breakpoint === 'pad';
  const showHamburger = isMobile || isPad;

  const handleMenuClick = (key: string) => {
    onNavigate(key);
    setDrawerOpen(false);
  };

  const navMenu = (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      items={menuItems}
      inlineCollapsed={isPad && !drawerOpen ? true : undefined}
      onClick={({ key }) => handleMenuClick(String(key))}
    />
  );

  return (
    <Layout className="mall-app-shell">
      {!isMobile && (
        <Sider
          width={248}
          collapsedWidth={64}
          collapsed={isPad}
          theme="light"
          className={`mall-sidebar${isPad ? ' mall-sidebar-rail' : ''}`}
        >
          <BrandBlock brand={brand} compact={isPad} />
          {isPad ? (
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              items={menuItems}
              inlineCollapsed
              onClick={({ key }) => handleMenuClick(String(key))}
            />
          ) : (
            navMenu
          )}
        </Sider>
      )}
      <Layout className="mall-main">
        <Header className="mall-topbar">
          <div className="mall-topbar-lead">
            {showHamburger && (
              <Button
                type="text"
                aria-label="打开菜单"
                className="mall-nav-trigger"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
              />
            )}
            <div className="mall-topbar-heading">
              <div className="mall-topbar-title">{topbarTitle}</div>
              <Typography.Text type="secondary" className="mall-topbar-subtitle">
                {topbarSubtitle}
              </Typography.Text>
            </div>
          </div>
          <Space size={12} className="mall-topbar-trailing">
            {contextPill}
            <Dropdown menu={userMenu} placement="bottomRight">
              <span className="mall-user-pill">
                <Avatar size={28} icon={<UserOutlined />} />
                <span className="mall-user-name">{userLabel}</span>
              </span>
            </Dropdown>
          </Space>
        </Header>
        <Content className="mall-content">{children}</Content>
      </Layout>
      <Drawer
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={264}
        className="mall-nav-drawer"
        styles={{ body: { padding: 0 } }}
        title={<BrandBlock brand={brand} />}
      >
        {navMenu}
      </Drawer>
      {extras}
    </Layout>
  );
}
